// Contrato comun de una "linea" telefonica. Hay dos implementaciones:
// - SipLine: JsSIP hacia la pasarela Asterisk, que registra al agente como su extension en 3CX.
// - SimLine: simulador para demostraciones y pruebas (nunca llama a numeros reales).

export type LineStatus = 'sin-configurar' | 'conectando' | 'en-linea' | 'error' | 'desconectado';

export type CallEvent = 'progress' | 'accepted' | 'ended' | 'failed';

export type EndInfo = { cause: string; byRemote: boolean };

export interface CallHandle {
  readonly id: string;
  readonly direction: 'in' | 'out';
  readonly remote: string; // numero o extension del otro lado
  readonly remoteName?: string;
  on(event: 'progress' | 'accepted', cb: () => void): void;
  on(event: 'ended' | 'failed', cb: (info: EndInfo) => void): void;
  answer(): void;
  // 486 = ocupado (la persona rechaza). 480 = no disponible: 3CX sigue timbrando sus otros dispositivos (D67).
  reject(codigo?: 480 | 486): void;
  hangup(): void;
  setMuted(muted: boolean): void;
  setHold(hold: boolean): void;
  sendDtmf(tone: string): void;
  transferBlind(target: string): Promise<void>;
  // Completa una transferencia con consulta: une a quien espera (this) con el consultado.
  transferAttended(consult: CallHandle): Promise<void>;
  // Hace sonar el audio de esta llamada por los parlantes (cuando hay dos a la vez).
  playAudio(): void;
}

export type LineConfig = {
  ws: string;
  usuario: string;
  clave: string;
  dominio?: string;
  nombre?: string;
};

export interface Line {
  readonly kind: 'sip' | 'sim';
  status: LineStatus;
  statusDetail: string;
  connect(cfg: LineConfig): void;
  disconnect(silent?: boolean): void;
  call(target: string): CallHandle;
  onStatus(cb: (s: LineStatus, detail: string) => void): () => void;
  onIncoming(cb: (call: CallHandle) => void): () => void;
}

// Emisor minimo de eventos, compartido por ambas lineas.
export class Emitter<E extends string> {
  private map = new Map<E, Set<(...a: never[]) => void>>();
  on(e: E, cb: (...a: never[]) => void) {
    if (!this.map.has(e)) this.map.set(e, new Set());
    this.map.get(e)!.add(cb);
    return () => this.map.get(e)?.delete(cb);
  }
  emit(e: E, ...a: unknown[]) {
    this.map.get(e)?.forEach((cb) => (cb as (...x: unknown[]) => void)(...a));
  }
}
